import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Gem, Shield, Tag, Star, Check } from 'lucide-react';
import { getProducts, categories, features, materials } from '../data/productStore';

const iconMap = { gem: Gem, shield: Shield, tag: Tag, star: Star };

const HomePage = () => {
  const sectionRefs = useRef([]);
  const products = getProducts();

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-in');
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
    );
    sectionRefs.current.forEach((ref) => ref && observer.observe(ref));
    return () => observer.disconnect();
  }, []);

  const addRef = (el) => {
    if (el && !sectionRefs.current.includes(el)) sectionRefs.current.push(el);
  };

  const featuredProducts = products.filter(p => p.featured);

  return (
    <div className="bg-[#0a0a0a] min-h-screen">
      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=1920&q=80')`,
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-[#0a0a0a]" />
        <div className="relative z-10 text-center px-4 max-w-4xl mx-auto">
          <h1
            className="text-5xl md:text-7xl lg:text-8xl font-bold text-white mb-4 leading-tight"
            style={{ fontFamily: 'Playfair Display, serif' }}
          >
            Luxury Accessories{' '}
            <span className="text-[#2EC866] block">Redefined</span>
          </h1>
          <p className="text-gray-300 text-lg md:text-xl mb-10 font-light">
            Premium accessories for watches, iPhones & more
          </p>
          <Link
            to="/shop"
            className="inline-flex items-center gap-2 bg-[#2EC866] hover:bg-[#25a855] text-white font-semibold px-8 py-4 rounded-lg text-lg transition-all duration-300 hover:shadow-[0_0_30px_rgba(46,200,102,0.3)] hover:-translate-y-0.5"
          >
            Explore Collection <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-[#0a0a0a] to-transparent" />
      </section>

      {/* Why Choose Section */}
      <section className="py-24 px-4" ref={addRef}>
        <div className="max-w-7xl mx-auto fade-section">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              Why choose Luxo-Market?
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              A premium end-to-end experience for giving and receiving exceptional moments
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((f, i) => {
              const Icon = iconMap[f.icon];
              return (
                <div
                  key={f.id}
                  className="bg-[#111] border border-gray-800/50 rounded-xl p-8 text-center hover:border-[#2EC866]/30 transition-all duration-500 hover:-translate-y-1 group"
                  style={{ animationDelay: `${i * 100}ms` }}
                >
                  <div className="w-14 h-14 bg-[#2EC866]/10 rounded-full flex items-center justify-center mx-auto mb-5 group-hover:bg-[#2EC866]/20 transition-colors duration-300">
                    <Icon className="w-6 h-6 text-[#2EC866]" />
                  </div>
                  <h3 className="text-white font-semibold text-lg mb-2">{f.title}</h3>
                  <p className="text-gray-500 text-sm leading-relaxed">{f.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Bracelet Collection */}
      <section className="py-24 px-4 bg-[#0f0f0f]" ref={addRef}>
        <div className="max-w-7xl mx-auto fade-section">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
                Exquisite Bracelet Collection
              </h2>
              <p className="text-gray-400 mb-8 leading-relaxed">
                Discover our handcrafted bracelet collection, featuring premium materials and timeless designs. Each piece is meticulously crafted to complement your luxury timepiece.
              </p>
              <ul className="space-y-4 mb-10">
                {['Premium stainless steel construction', 'Adjustable sizing for perfect fit', 'Water-resistant and durable finish'].map(item => (
                  <li key={item} className="flex items-center gap-3 text-gray-300">
                    <Check className="w-5 h-5 text-[#2EC866] flex-shrink-0" />
                    <span className="text-sm">{item}</span>
                  </li>
                ))}
              </ul>
              <Link
                to="/shop?category=watch-straps"
                className="inline-flex items-center gap-2 bg-[#2EC866] hover:bg-[#25a855] text-white font-semibold px-6 py-3 rounded-lg transition-all duration-300 hover:shadow-[0_0_20px_rgba(46,200,102,0.2)]"
              >
                Shop Bracelets <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="relative">
              <div className="rounded-2xl overflow-hidden">
                <img
                  src="https://www.pass-lux.com/wp-content/uploads/2025/07/bracelets.webp?auto=compress&cs=tinysrgb&w=800"
                  alt="Luxury Bracelet Collection"
                  className="w-full h-[450px] object-cover rounded-2xl hover:scale-105 transition-transform duration-700"
                />
              </div>
              <div className="absolute top-4 right-4 bg-[#2EC866] text-white text-xs font-bold px-3 py-1.5 rounded-full">
                New Collection
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Shop by Category */}
      <section className="py-24 px-4" ref={addRef}>
        <div className="max-w-7xl mx-auto fade-section">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              Shop by Category
            </h2>
            <p className="text-gray-400">Explore our carefully curated collections</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {categories.map((cat, i) => (
              <Link
                key={cat.id}
                to={`/shop?category=${cat.slug}`}
                className="group relative rounded-xl overflow-hidden h-80 block"
                style={{ animationDelay: `${i * 100}ms` }}
              >
                <img
                  src={cat.image}
                  alt={cat.name}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <h3 className="text-white font-bold text-lg mb-1">{cat.name}</h3>
                  <p className="text-gray-400 text-sm mb-3">{cat.description}</p>
                  <span className="inline-flex items-center gap-1 text-[#2EC866] text-sm font-medium group-hover:gap-2 transition-all duration-300">
                    Explore <ArrowRight className="w-4 h-4" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Collection */}
      <section className="py-24 px-4 bg-[#0f0f0f]" ref={addRef}>
        <div className="max-w-7xl mx-auto fade-section">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              Featured Collection
            </h2>
            <p className="text-gray-400">Discover our most coveted pieces, carefully curated for the discerning collector</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {featuredProducts.map((product, i) => (
              <Link
                key={product.id}
                to={`/product/${product.slug}`}
                className="group bg-[#111] border border-gray-800/50 rounded-xl overflow-hidden hover:border-[#2EC866]/20 transition-all duration-500"
                style={{ animationDelay: `${i * 80}ms` }}
              >
                <div className="relative overflow-hidden">
                  <img
                    src={product.image}
                    alt={product.name}
                    className="w-full h-56 object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300" />
                </div>
                <div className="p-5">
                  <span className="text-[#2EC866] text-xs font-medium uppercase tracking-wider">{product.categoryName}</span>
                  <h3 className="text-white font-semibold mt-2 mb-3 text-sm group-hover:text-[#2EC866] transition-colors duration-300 line-clamp-2">
                    {product.name}
                  </h3>
                  <p className="text-white font-bold text-lg">&pound;{product.price.toFixed(2)}</p>
                </div>
              </Link>
            ))}
          </div>
          <div className="text-center mt-12">
            <Link
              to="/shop"
              className="inline-flex items-center gap-2 border border-[#2EC866] text-[#2EC866] hover:bg-[#2EC866] hover:text-white font-semibold px-8 py-3 rounded-lg transition-all duration-300"
            >
              View All Products <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Premium Materials */}
      <section className="py-24 px-4" ref={addRef}>
        <div className="max-w-7xl mx-auto fade-section">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              Premium Materials
            </h2>
            <p className="text-gray-400">We source only the finest materials to ensure exceptional quality and longevity</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {materials.map((mat, i) => (
              <div
                key={mat.id}
                className="bg-[#111] border border-gray-800/50 rounded-xl p-8 hover:border-[#2EC866]/30 transition-all duration-500 hover:-translate-y-1"
                style={{ animationDelay: `${i * 150}ms` }}
              >
                <div className="w-12 h-12 bg-[#2EC866]/10 rounded-lg flex items-center justify-center mb-5">
                  <Gem className="w-5 h-5 text-[#2EC866]" />
                </div>
                <h3 className="text-white font-bold text-lg mb-3">{mat.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{mat.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-4 bg-[#0f0f0f]" ref={addRef}>
        <div className="max-w-4xl mx-auto text-center fade-section">
          <h2 className="text-3xl md:text-5xl font-bold text-white mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
            Elevate Your Style
          </h2>
          <p className="text-gray-400 text-lg mb-10 max-w-2xl mx-auto">
            Join thousands of satisfied customers who trust Luxo-Market for their luxury accessory needs
          </p>
          <Link
            to="/account"
            className="inline-flex items-center gap-2 bg-[#2EC866] hover:bg-[#25a855] text-white font-semibold px-8 py-4 rounded-lg text-lg transition-all duration-300 hover:shadow-[0_0_30px_rgba(46,200,102,0.3)] hover:-translate-y-0.5"
          >
            Create Account <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
